export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  top: number;
  left: number;
}

/** 弹窗与触发按钮之间的间距，同时决定「飞出」动画的距离感。 */
export const POPOVER_GAP = 10;

/** 弹窗与视口边缘的最小间距。 */
export const VIEWPORT_MARGIN = 8;

export const POPOVER_WIDTH = 340;
export const POPOVER_MIN_WIDTH = 280;
export const POPOVER_MAX_HEIGHT = 420;

/**
 * 断点分桶。
 *
 * 弹窗位置缓存的键里必须带上分桶：布局变化后旧位置会失效，
 * 但**同一个分桶内**位置必须保持稳定，这就是「弹窗位置不可变」的实现方式。
 */
export type BreakpointBucket = 'xs' | 'sm' | 'md' | 'lg';

export function breakpointBucket(width: number): BreakpointBucket {
  if (width < 480) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1280) return 'md';
  return 'lg';
}

/** 相邻边优先：默认向下，空间不足再向上，最后才考虑左右。 */
const SIDE_PRIORITY: readonly PopoverSide[] = ['bottom', 'top', 'right', 'left'];

function spaceFor(side: PopoverSide, anchor: Rect, viewport: Size): number {
  switch (side) {
    case 'bottom':
      return viewport.height - (anchor.top + anchor.height) - POPOVER_GAP - VIEWPORT_MARGIN;
    case 'top':
      return anchor.top - POPOVER_GAP - VIEWPORT_MARGIN;
    case 'right':
      return viewport.width - (anchor.left + anchor.width) - POPOVER_GAP - VIEWPORT_MARGIN;
    case 'left':
      return anchor.left - POPOVER_GAP - VIEWPORT_MARGIN;
  }
}

function requiredFor(side: PopoverSide, size: Size): number {
  return side === 'bottom' || side === 'top' ? size.height : size.width;
}

/**
 * 选择弹窗所在的一侧。
 *
 * `preferred` 是缓存里记录过的历史选择：只要它仍然放得下就继续用它，
 * 这样同一个按钮的弹窗位置在同一个断点下**永远不会漂移**，
 * 而不会因为某次内容变高就突然跳到另一边。
 */
export function resolveSide(
  anchor: Rect,
  size: Size,
  viewport: Size,
  preferred?: PopoverSide,
): PopoverSide {
  if (preferred && spaceFor(preferred, anchor, viewport) >= requiredFor(preferred, size)) {
    return preferred;
  }
  for (const side of SIDE_PRIORITY) {
    if (spaceFor(side, anchor, viewport) >= requiredFor(side, size)) return side;
  }
  // 四周都放不下时退化为「向下 + 视口裁剪」，至少保证可读。
  return 'bottom';
}

/** 与锚点相邻的那个角，用于设置 transform-origin，让缩放看起来是从按钮长出来的。 */
export function originCornerFor(side: PopoverSide): string {
  switch (side) {
    case 'bottom':
      return 'top left';
    case 'top':
      return 'bottom left';
    case 'right':
      return 'top left';
    case 'left':
      return 'top right';
  }
}

/** 飞出动画的起点：元素上离锚点最近的点。 */
export function flightOrigin(side: PopoverSide, anchor: Rect): Point {
  switch (side) {
    case 'bottom':
      return { top: anchor.top + anchor.height, left: anchor.left + anchor.width / 2 };
    case 'top':
      return { top: anchor.top, left: anchor.left + anchor.width / 2 };
    case 'right':
      return { top: anchor.top + anchor.height / 2, left: anchor.left + anchor.width };
    case 'left':
      return { top: anchor.top + anchor.height / 2, left: anchor.left };
  }
}

/** 元素上离锚点最近的点。用来算 FLIP 的位移量。 */
function nearestPointOnPopover(side: PopoverSide, position: Point, size: Size): Point {
  switch (side) {
    case 'bottom':
      return { top: position.top, left: position.left + size.width / 2 };
    case 'top':
      return { top: position.top + size.height, left: position.left + size.width / 2 };
    case 'right':
      return { top: position.top + size.height / 2, left: position.left };
    case 'left':
      return { top: position.top + size.height / 2, left: position.left + size.width };
  }
}

/**
 * 计算弹窗尺寸。
 *
 * 必须优先使用布局尺寸（offsetWidth/offsetHeight）：入场动画带着 scale(0.82)，
 * getBoundingClientRect() 会把尺寸算小，340px 宽的弹窗会被当成 280px 去排位，
 * 于是被顶出视口右边缘（实测溢出 52px）。
 */
export function popoverSize(layout: Size, rect: Size): Size {
  return {
    width: Math.max(POPOVER_MIN_WIDTH, layout.width || rect.width || POPOVER_WIDTH),
    height: Math.min(POPOVER_MAX_HEIGHT, layout.height || rect.height || 160),
  };
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}

export interface Placement {
  side: PopoverSide;
  position: Point;
  origin: string;
  /** FLIP 起始位移：从触发按钮飞到最终位置要平移的量。 */
  delta: Point;
}

/**
 * 计算弹窗的最终位置与动画起点。
 *
 * 纯函数：给定同样的输入永远得到同样的输出，因此同一个按钮在同一断点下
 * 弹窗出现的位置是确定的（不依赖实时碰撞检测）。
 */
export function computePlacement(
  anchor: Rect,
  size: Size,
  viewport: Size,
  preferred?: PopoverSide,
): Placement {
  const side = resolveSide(anchor, size, viewport, preferred);

  let top = 0;
  let left = 0;
  switch (side) {
    case 'bottom':
      top = anchor.top + anchor.height + POPOVER_GAP;
      left = anchor.left + anchor.width / 2 - size.width / 2;
      break;
    case 'top':
      top = anchor.top - POPOVER_GAP - size.height;
      left = anchor.left + anchor.width / 2 - size.width / 2;
      break;
    case 'right':
      top = anchor.top + anchor.height / 2 - size.height / 2;
      left = anchor.left + anchor.width + POPOVER_GAP;
      break;
    case 'left':
      top = anchor.top + anchor.height / 2 - size.height / 2;
      left = anchor.left - POPOVER_GAP - size.width;
      break;
  }

  const position: Point = {
    top: clamp(top, VIEWPORT_MARGIN, viewport.height - size.height - VIEWPORT_MARGIN),
    left: clamp(left, VIEWPORT_MARGIN, viewport.width - size.width - VIEWPORT_MARGIN),
  };

  const origin = flightOrigin(side, anchor);
  const nearest = nearestPointOnPopover(side, position, size);

  return {
    side,
    position,
    origin: originCornerFor(side),
    delta: { top: origin.top - nearest.top, left: origin.left - nearest.left },
  };
}
